import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import Card from "components/Card";
import { Label, Select, TextInput } from "components/Form";
import { Grid } from "components/Containers";
import { APPLICANT_LIST_FILTERS_CONFIG, DIRECTION } from "constants/index";
import { toUcFirst } from "utils";
import { AdvancedFilters } from "components/AdvancedFilters";

/**
 * ApplicantListControls
 *
 * @param {Boolean}   hideKeywordSearch
 * @param {Boolean}   hideMemberFilter
 * @param {Boolean}   hideSorts
 * @param {Boolean}   hideAdvancedFilters
 * @param {Boolean}   hideSortDirection
 * @param {Object}    selected
 * @param {Function}  setSelected
 */
const ApplicantListControls = ({
  hideKeywordSearch,
  hideMemberFilter,
  hideSorts,
  hideAdvancedFilters,
  hideSortDirection,
  selected,
  setSelected,
  hasKeywordSearch,
  sortingOptions,
  ...props
}) => {
  const [keyword, setKeyword] = useState(selected?.keyword);
  const debouncedSetKeyword = useMemo(
    () => debounce((val) => setSelected((prev) => ({ ...prev, keyword: val })), 500),
    [setSelected]
  );

  useEffect(() => {
    setKeyword(selected?.keyword);
  }, [selected?.keyword]);

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const handleSearchInputChange = (val) => {
    setKeyword(val);
    debouncedSetKeyword(val);
  };

  return (
    <Card>
      <Grid>
        {!hideKeywordSearch && (
          <Grid.col colSpan={3}>
            <Label>
              <FormattedMessage id="Global.KeywordSearch.Label" />
            </Label>
            <TextInput
              name="keyword"
              value={keyword}
              placeholder="Search by keyword"
              maxLength={50}
              onChange={(_, val) => handleSearchInputChange(val)}
            />
          </Grid.col>
        )}
        {!hideMemberFilter && (
          <Grid.col colSpan={3}>
            <Label>
              <FormattedMessage id="Missions.Applicants.ApplicantListControls.MemberFilter.Label" />
            </Label>
            <Select
              value={selected?.isMember}
              placeholder="Profile Status"
              onChange={(_, val) => setSelected((prev) => ({ ...prev, isMember: val }))}
              valueKey="id"
              labelKey="name"
              options={[
                { name: "Member", id: true },
                { name: "Candidate", id: false },
              ]}
              isClearable
            />
          </Grid.col>
        )}
        {!hideSorts && (
          <>
            <Grid.col colSpan={3}>
              <Label>
                <FormattedMessage id="Global.SortBy" />
              </Label>
              <Select
                name="sortKey"
                value={selected?.sortKey}
                options={sortingOptions}
                onChange={(name, val) => setSelected((prev) => ({ ...prev, [name]: val }))}
                isClearable={false}
                disabled={hasKeywordSearch}
              />
            </Grid.col>
            {!hideSortDirection && (
              <Grid.col colSpan={3}>
                <Label>
                  <FormattedMessage id="Global.SortDirection" />
                </Label>
                <Select
                  name="sortDirection"
                  value={selected?.sortDirection}
                  options={Object.entries(DIRECTION).map(([label, value]) => ({ label: toUcFirst(label), value }))}
                  onChange={(name, val) => setSelected((prev) => ({ ...prev, [name]: val }))}
                  isClearable={false}
                  disabled={hasKeywordSearch}
                />
              </Grid.col>
            )}
          </>
        )}
      </Grid>
      {!hideAdvancedFilters && (
        <AdvancedFilters
          selected={selected}
          setSelected={setSelected}
          filterConfigMap={APPLICANT_LIST_FILTERS_CONFIG}
          {...props}
        />
      )}
    </Card>
  );
};

ApplicantListControls.propTypes = {
  hideKeywordSearch: PropTypes.bool,
  hideMemberFilter: PropTypes.bool,
  hideSorts: PropTypes.bool,
  hideAdvancedFilters: PropTypes.bool,
  hideSortDirection: PropTypes.bool,
  selected: PropTypes.object,
  setSelected: PropTypes.func,
  hasKeywordSearch: PropTypes.bool,
  sortingOptions: PropTypes.array,
};

export default ApplicantListControls;
